import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS } from '../../../U3141';
import U2000Panel from '../../../../u2000Core/components/layout/U2000Panel';
import { useTranslation } from 'react-i18next';
import { U3141LotStateEnum } from '../../common/U3141LotStateType';
import U2000RadioGroupString from '../../../../u2000Core/components/formControls/radio/U2000RadioGroupString';
import type { U3141ChildLotChain } from '../../../U3141Service';
import { U3141ChildLotStateEnum } from '../../common/U3141ChildLotStateType';
import U2000InputText from '../../../../u2000Core/components/formControls/input/U2000InputText';
import U2000Label from '../../../../u2000Core/components/formControls/label/U2000Label';
import { formatConcordanceEntryForDisplay } from './U3141ConcordanceUtils';
import { useRef } from 'react';
import U3141ChildLotChainLotFields from './U3141ChildLotChainLotFields';

interface U3141ChildLotChainBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3141ChildLotChain>;
}

function U3141ChildLotChainBody({ formStateHandlers }: U3141ChildLotChainBodyProps) {
  const { t, i18n } = useTranslation();

  const lotDetail = formStateHandlers.getData()?.lotDetail;
  const concordanceOrigines = lotDetail?.concordanceOrigines ?? [];
  const parentContext = useRef({
    codeDistrict: lotDetail?.district?.codeDistrict,
    cadastreCode: lotDetail?.cadastre?.cadastreCode,
  });
  parentContext.current = {
    codeDistrict: lotDetail?.district?.codeDistrict,
    cadastreCode: lotDetail?.cadastre?.cadastreCode,
  };

  return (
    <div className="flex h-full items-stretch gap-4">
      <div className="flex w-full flex-[66_66_0%] flex-row gap-4">
        <U3141ChildLotChainLotFields formStateHandlers={formStateHandlers} mode="form" />
        <div className="flex flex-1 flex-col gap-2">
          <div className="space-y-1.5">
            <U2000Label label={t('lblLotDetail.concordanceOrigines', { ns: NS })} />
            <div className="flex min-h-[46px] w-full flex-wrap items-center gap-1.5 rounded-lg border border-gray-300 bg-muted px-4 py-2 text-sm">
              {Array.isArray(concordanceOrigines) && concordanceOrigines.length > 0 ? (
                concordanceOrigines.map((entry: string, idx: number) => {
                  const label = formatConcordanceEntryForDisplay(entry, parentContext.current);
                  const tagClasses =
                    'inline-flex max-w-xs items-center rounded-full border border-gray-200 bg-muted px-2.5 py-1 text-xs font-medium text-gray-600 transition-all duration-150';
                  return (
                    <span key={`${idx}-orig-${entry}`} className={tagClasses} title={label}>
                      {label}
                    </span>
                  );
                })
              ) : (
                <span className="text-gray-500 italic">{t('lblConcordanceOriginesEmpty', { ns: NS })}</span>
              )}
            </div>
          </div>

          {(() => {
            const lang = i18n?.language || 'fr';
            const options = U3141LotStateEnum.map((type) => ({
              value: type.value,
              label: typeof type.label === 'object' ? (type.label as Record<string, string>)[lang] : type.label,
            }));
            return (
              <div className="mt-4">
                <U2000RadioGroupString
                  titleGroup={t('lblLotDetail.lotStateVerification', { ns: NS })}
                  fieldName="lotDetail.lotState"
                  orientation="horizontal"
                  options={options}
                  formStateHandlers={formStateHandlers}
                  readonly={true}
                />
              </div>
            );
          })()}

          {(() => {
            const lang = i18n?.language || 'fr';
            const options = U3141ChildLotStateEnum.map((type) => ({
              value: type.value,
              label: typeof type.label === 'object' ? (type.label as Record<string, string>)[lang] : type.label,
            }));
            return (
              <div className="mt-4">
                <U2000RadioGroupString
                  titleGroup={t('lblLotDetail.lotStateCreation', { ns: NS })}
                  fieldName="childLotState"
                  orientation="horizontal"
                  options={options}
                  formStateHandlers={formStateHandlers}
                  readonly={true}
                />
              </div>
            );
          })()}

          <U2000Panel title={t('lblLotDetail.pnlHistoricalChange', { ns: NS })} titleAlign="left" className="mt-4 flex h-full w-full flex-col items-start justify-start">
            <div className="mt-1 flex w-full flex-row gap-4">
              <div className="flex-1">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName="lotDetail.createdByUser" label={t('lblLotDetail.CreatedByUser', { ns: NS })} readonly={true} />
              </div>
              <div className="flex-1">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName="lotDetail.createdAt" label={t('lblLotDetail.CreatedAt', { ns: NS })} readonly={true} />
              </div>
            </div>
            <div className="mt-1 flex w-full flex-row gap-4">
              <div className="flex-1">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName="lotDetail.lastModifiedByUser" label={t('lblLotDetail.lastModifiedByUser', { ns: NS })} readonly={true} />
              </div>
              <div className="flex-1">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName="lotDetail.lastModifiedAt" label={t('lblLotDetail.lastModifiedAt', { ns: NS })} readonly={true} />
              </div>
            </div>
          </U2000Panel>
        </div>
      </div>
    </div>
  );
}

export default U3141ChildLotChainBody;
